import { useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ChevronRight, CircleCheck, Info, Layers, Plus } from 'lucide-react';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { GuidanceCard } from '@/components/compliance/ControlGuidance';
import { useAddControlToWorkspace, useLibraryControl } from '@/api/hooks/useControlLibrary';
import { CONTROL_GUIDANCE_FIELDS, authoredLines, hasText } from '@/lib/control-guidance';
import { cn } from '@/lib/utils';
import { CONTROL_LIBRARY_PATH } from './ControlLibraryPage';

/**
 * CONTROL LIBRARY — the DETAIL WINDOW for one control (`/compliance/control-library/:reference`).
 *
 * ⚠ A ROUTE, NOT LOCAL STATE. The window is a child route of the library, so the list stays mounted beneath it
 *   (search and filters survive), a link to one control works, and Back closes it.
 * ⚠ AN ACCESSIBLE DIALOG. The `Sheet` is a Radix Dialog: role="dialog", focus contained while open, Escape and the
 *   overlay close it, the title names it. aria-modal is stated explicitly. Focus goes to the window on open and
 *   back to the row that opened it on close.
 * ⚠ READ-ONLY, PUBLISHED CONTENT ONLY. Everything shown comes from the single-control read, which the server
 *   answers from the library list's own query (same eligibility, same "in my workspace"). The durable reference is
 *   the URL key and is never shown as text; the display code is what a person reads.
 *
 * Layout (MI preview feedback): header (code, title, domain → family) · an at-a-glance strip · one card per
 * authored section · a pinned footer carrying the workspace status and the next action.
 */

/** The row control that opened this window, found by the reference it carries. */
function openerFor(reference: string | undefined): HTMLElement | null {
  if (!reference) return null;
  for (const el of document.querySelectorAll<HTMLElement>('[data-library-open]')) {
    if (el.dataset.libraryOpen === reference) return el;
  }
  return null;
}

/** The workspace status as a chip: green when the control is in the workspace, neutral when it can be added. */
function StatusChip({ inWorkspace, testId }: { inWorkspace: boolean; testId?: string }) {
  return inWorkspace ? (
    <span
      data-testid={testId ? `${testId}-in-workspace` : undefined}
      className="inline-flex items-center gap-2 rounded-full border border-cert-valid-border bg-cert-valid-bg px-3 py-1 text-[12.5px] font-medium text-cert-valid-fg"
    >
      <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" />
      In workspace
    </span>
  ) : (
    <span
      data-testid={testId ? `${testId}-available` : undefined}
      className="inline-flex items-center rounded-full border border-cert-neutral-border bg-cert-neutral-bg px-3 py-1 text-[12.5px] font-medium text-cert-neutral-fg"
    >
      Available to add
    </span>
  );
}

const QUIET_BUTTON = 'rounded-lg border border-border-subtle px-3 py-2 text-[13px] text-text-muted hover:bg-white/[0.04]';

export default function LibraryControlSheet() {
  const { reference } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  const { data: control, isLoading, isError, refetch } = useLibraryControl(reference);
  const add = useAddControlToWorkspace();
  const contentRef = useRef<HTMLDivElement>(null);
  // Whatever held focus before the window took it — the fallback when the opening row is no longer on screen.
  const previousFocus = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement);

  const close = () => navigate(CONTROL_LIBRARY_PATH);

  // ⚠ Held as "adding" until the server's own answer says In workspace — the mutation settling first would
  //   otherwise flash the Add button back for the moment between success and the refetch.
  const adding = add.isPending || (add.isSuccess && control?.in_workspace === false);

  const sections = control
    ? [
      { key: 'description' as const, label: 'Description', text: control.description },
      ...CONTROL_GUIDANCE_FIELDS.map((f) => ({ key: f.key, label: f.label, text: control[f.key] })),
    ].filter((s) => hasText(s.text))
    : [];

  // ⓘ Counted only when the author wrote the evidence as a LIST (several lines): a single line is a description of
  //   the evidence, not one item, and a count of 1 would claim a precision the text does not have.
  const evidenceItems = control && hasText(control.expected_evidence) ? authoredLines(control.expected_evidence) : [];
  // ⓘ The workspace status is NOT repeated here (MI): it lives once, in the footer, beside the action it governs.
  const glance = control
    ? [
      { label: 'Domain', value: <span className="font-mono">{control.domain_public_id}</span> },
      { label: 'Family', value: <span className="font-mono">{control.family_public_id}</span> },
      ...(evidenceItems.length > 1
        ? [{ label: 'Evidence items', value: <span className="tabular-nums">{evidenceItems.length}</span> }]
        : []),
    ]
    : [];

  return (
    <Sheet open onOpenChange={(open) => { if (!open) close(); }}>
      <SheetContent
        ref={contentRef}
        aria-modal="true"
        data-testid="library-control-window"
        // ⓘ The container takes focus on open (so the dialog is announced) but is not a control itself: the global
        //   focus-visible ring is suppressed on the panel only — it appears on the first control a Tab reaches.
        className="w-full gap-0 bg-bg-base p-0 focus-visible:ring-0 focus-visible:ring-offset-0 sm:max-w-[600px]"
        onOpenAutoFocus={(e) => { e.preventDefault(); contentRef.current?.focus(); }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          const back = openerFor(reference)
            ?? (previousFocus.current instanceof HTMLElement && previousFocus.current.isConnected
              ? previousFocus.current : null);
          back?.focus();
        }}
      >
        <SheetHeader className="gap-2 space-y-0 border-b border-border-subtle bg-gradient-to-b from-bg-surface-2 to-bg-surface p-6 pr-14">
          {control && (
            <span className="w-fit rounded bg-accent-cyan/15 px-2 py-0.5 font-mono text-[12px] text-accent-cyan">
              {control.display_code}
            </span>
          )}
          <SheetTitle className="text-[22px] leading-tight [text-wrap:balance]">
            {control?.title
              ?? (control === null ? 'Control not available' : isError ? 'Control details' : 'Loading control…')}
          </SheetTitle>
          <SheetDescription
            data-testid="library-control-breadcrumb"
            className="flex flex-wrap items-center gap-2 text-[13px] text-text-muted"
          >
            {control ? (
              <>
                <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{control.domain_name}</span>
                <span className="sr-only">, </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-faint" aria-hidden="true" />
                <span>{control.family_name}</span>
              </>
            ) : 'Control Library'}
          </SheetDescription>
        </SheetHeader>

        {control && (
          <dl
            data-testid="library-control-glance"
            className={cn('grid grid-cols-2 gap-4 border-b border-border-subtle bg-bg-surface px-6 py-4',
              glance.length === 3 && 'sm:grid-cols-3')}
          >
            {glance.map((g) => (
              <div key={g.label} className="min-w-0">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-text-muted">{g.label}</dt>
                <dd className="mt-1 truncate text-[13px] text-text-primary">{g.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {isLoading ? (
            <div className="flex flex-col gap-4" data-testid="library-control-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-lg border border-border-subtle bg-bg-surface p-4">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="mt-4 h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-4/5" />
                </div>
              ))}
            </div>
          ) : control === null ? (
            // ⚠ One answer for unknown, unpublished, retired and mid-revise alike — the server does not say
            //   which, and neither does this.
            <div data-testid="library-control-unavailable" className="flex flex-col items-start gap-4">
              <p className="text-[14px] text-text-muted">This control isn&apos;t available in the library.</p>
              <button type="button" onClick={close} className={QUIET_BUTTON}>Back to the library</button>
            </div>
          ) : isError || !control ? (
            <div role="alert" data-testid="library-control-error" className="flex flex-col items-start gap-4">
              <p className="text-[14px] text-text-muted">Couldn&apos;t load this control.</p>
              <button type="button" onClick={() => { void refetch(); }} className={QUIET_BUTTON}>Try again</button>
            </div>
          ) : sections.length === 0 ? (
            <div
              data-testid="library-control-no-detail"
              className="flex items-start gap-2 rounded-lg border border-dashed border-border-subtle bg-bg-surface p-4"
            >
              <Info className="mt-[3px] h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
              <p className="text-[14px] text-text-muted">No further detail has been published for this control.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {sections.map((s) => (
                <GuidanceCard key={s.key} kind={s.key} label={s.label} text={s.text ?? ''}
                  testId={`library-control-${s.key}`} />
              ))}
            </div>
          )}
        </div>

        {control && (
          // ⓘ A clear hairline (MI): the footer is a pinned bar, so the space above it on a short control reads
          //   as the end of the content rather than as a gap.
          <div className="border-t border-white/10 bg-bg-surface px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <StatusChip inWorkspace={control.in_workspace} testId="library-control" />
              {control.in_workspace ? (
                // ⓘ Only once it is in the workspace: the control page exists for a tenant's own controls.
                <Link
                  to={`/compliance/workspace/controls/${encodeURIComponent(control.control_reference)}`}
                  className="inline-flex items-center gap-2 rounded-sm text-[13px] font-medium text-accent-cyan hover:underline"
                >
                  Open full control page
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              ) : (
                <button
                  type="button"
                  disabled={adding}
                  onClick={() => add.mutate(control.control_reference)}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent-cyan px-4 py-2 text-[13px] font-medium text-[#06101f] disabled:opacity-60"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {adding ? 'Adding…' : 'Add to workspace'}
                </button>
              )}
            </div>
            {add.isError && (
              <p role="alert" className="mt-2 text-[12.5px] text-cert-warn-fg">
                Couldn&apos;t add this control. Try again.
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
